import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DashboardLogoUploadSchema,
  DashboardRestaurantListSchema,
  DashboardRestaurantProfileSchema,
  DashboardRestaurantSchema,
  RestaurantHoursSchema,
  type DashboardRestaurant,
  type OpeningWindow,
  type RestaurantProfile,
} from "@bytebite/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiGet, apiPatch, apiPut, apiUpload, ApiError } from "@/lib/api";
import { WEEKDAY_LABELS, draftToWindows, hoursToDraft, type DayWindows } from "@/lib/hours";
import { readStoredRestaurantId, storeRestaurantId } from "@/lib/restaurant-context";

const fieldClass =
  "border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-sm";

export function RestaurantPage() {
  const [restaurants, setRestaurants] = useState<DashboardRestaurant[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [windows, setWindows] = useState<OpeningWindow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const selected = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === restaurantId) ?? null,
    [restaurantId, restaurants],
  );

  const loadHours = useCallback(async (id: string) => {
    const payload = RestaurantHoursSchema.parse(
      await apiGet<unknown>(`/dashboard/restaurants/${id}/hours`),
    );
    setWindows(payload.windows);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const list = DashboardRestaurantListSchema.parse(
          await apiGet<unknown>("/dashboard/restaurants"),
        );
        if (cancelled) return;
        setRestaurants(list.restaurants);
        const stored = readStoredRestaurantId(list.restaurants[0]?.id ?? null);
        const next =
          list.restaurants.find((restaurant) => restaurant.id === stored)?.id ??
          list.restaurants[0]?.id ??
          null;
        setRestaurantId(next);
        if (next) {
          storeRestaurantId(next);
          await loadHours(next);
        }
      } catch {
        if (!cancelled) setError("Could not load restaurants.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [loadHours]);

  async function changeRestaurant(id: string) {
    setRestaurantId(id);
    storeRestaurantId(id);
    setError(null);
    try {
      await loadHours(id);
    } catch {
      setError("Could not load opening hours.");
    }
  }

  async function uploadLogo(file: File) {
    if (!restaurantId) return;
    setBusy(true);
    setError(null);
    try {
      const result = DashboardLogoUploadSchema.parse(
        await apiUpload<unknown>(`/dashboard/restaurants/${restaurantId}/logo`, file),
      );
      setRestaurants((current) =>
        current.map((restaurant) =>
          restaurant.id === restaurantId ? { ...restaurant, logoUrl: result.logoUrl } : restaurant,
        ),
      );
    } catch {
      setError("The logo could not be uploaded.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading restaurant…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Restaurant</h1>
          <p className="text-muted-foreground text-sm">
            Logo, public profile, weekly opening hours, and the order pause for the selected
            restaurant. Checkout is rejected while the restaurant is closed or paused.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Restaurant</span>
          <select
            className={fieldClass}
            value={restaurantId ?? ""}
            onChange={(event) => void changeRestaurant(event.target.value)}
          >
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
      {selected && restaurantId ? (
        <>
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle>{selected.name}</CardTitle>
              <CardDescription>
                Optional logo. Empty files do not clear the current URL.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {selected.logoUrl ? (
                <img
                  src={selected.logoUrl}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <p className="text-muted-foreground text-sm">No logo uploaded yet.</p>
              )}
              <label className="flex flex-col gap-2 text-sm">
                Upload logo
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) void uploadLogo(file);
                  }}
                />
              </label>
            </CardContent>
          </Card>
          <ProfileEditor restaurantId={restaurantId} />
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle>Order pause</CardTitle>
              <CardDescription>
                Weekly hours stay as they are. Guests can still browse the menu.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.ordersPaused}
                  disabled={busy}
                  onChange={(event) => {
                    const ordersPaused = event.target.checked;
                    setBusy(true);
                    setError(null);
                    void apiPatch<unknown>(`/dashboard/restaurants/${restaurantId}`, {
                      ordersPaused,
                    })
                      .then((payload) => {
                        const updated = DashboardRestaurantSchema.parse(payload);
                        setRestaurants((current) =>
                          current.map((restaurant) =>
                            restaurant.id === updated.id ? updated : restaurant,
                          ),
                        );
                      })
                      .catch(() => setError("The order pause could not be saved."))
                      .finally(() => setBusy(false));
                  }}
                />
                Pause orders
              </label>
            </CardContent>
          </Card>
          <HoursEditor
            restaurantId={restaurantId}
            restaurantName={selected.name}
            windows={windows}
            onSaved={setWindows}
          />
        </>
      ) : (
        <p className="text-muted-foreground text-sm">No restaurants available.</p>
      )}
    </div>
  );
}

const emptyProfileDraft = {
  description: "",
  street: "",
  houseNumber: "",
  postalCode: "",
  city: "",
  phone: "",
  email: "",
  latitude: "",
  longitude: "",
  mapUrl: "",
};

type ProfileDraft = typeof emptyProfileDraft;

function profileToDraft(profile: RestaurantProfile | null): ProfileDraft {
  if (!profile) return emptyProfileDraft;
  return {
    description: profile.description,
    street: profile.street,
    houseNumber: profile.houseNumber,
    postalCode: profile.postalCode,
    city: profile.city,
    phone: profile.phone,
    email: profile.email,
    latitude: profile.latitude === null ? "" : String(profile.latitude),
    longitude: profile.longitude === null ? "" : String(profile.longitude),
    mapUrl: profile.mapUrl ?? "",
  };
}

function coordinatesFromDraft(
  latitude: string,
  longitude: string,
): { latitude: number | null; longitude: number | null } | null {
  const lat = latitude.trim();
  const lng = longitude.trim();
  if (lat === "" && lng === "") return { latitude: null, longitude: null };
  if (lat === "" || lng === "") return null;
  const latitudeNumber = Number(lat);
  const longitudeNumber = Number(lng);
  if (!Number.isFinite(latitudeNumber) || !Number.isFinite(longitudeNumber)) return null;
  return { latitude: latitudeNumber, longitude: longitudeNumber };
}

function ProfileEditor({ restaurantId }: { restaurantId: string }) {
  const [draft, setDraft] = useState<ProfileDraft>(emptyProfileDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage(null);
    setError(null);
    void apiGet<unknown>(`/dashboard/restaurants/${restaurantId}/profile`)
      .then((payload) => {
        if (cancelled) return;
        const parsed = DashboardRestaurantProfileSchema.parse(payload);
        setDraft(profileToDraft(parsed.profile));
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the restaurant profile.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  function update(field: keyof ProfileDraft, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function save() {
    const coordinates = coordinatesFromDraft(draft.latitude, draft.longitude);
    if (!coordinates) {
      setError("Enter both latitude and longitude, or leave both empty.");
      setMessage(null);
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = DashboardRestaurantProfileSchema.parse(
        await apiPut<unknown>(`/dashboard/restaurants/${restaurantId}/profile`, {
          description: draft.description,
          street: draft.street,
          houseNumber: draft.houseNumber,
          postalCode: draft.postalCode,
          city: draft.city,
          countryCode: "DE",
          phone: draft.phone,
          email: draft.email,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          mapUrl: draft.mapUrl.trim() === "" ? null : draft.mapUrl,
        }),
      );
      setDraft(profileToDraft(payload.profile));
      setMessage("Profile saved.");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 400) {
        setError(
          "Check the address, a five-digit postal code, phone, email, and an http(s) map link.",
        );
      } else {
        setError("Could not save the restaurant profile.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Public profile</CardTitle>
        <CardDescription>
          Address and contact shown on the storefront. One location, in Germany. Latitude and
          longitude are optional and must be saved together.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading profile…</p>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-sm">
              Short description
              <textarea
                className={fieldClass}
                rows={3}
                value={draft.description}
                onChange={(event) => update("description", event.target.value)}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
              <label className="flex flex-col gap-1 text-sm">
                Street
                <input
                  className={fieldClass}
                  value={draft.street}
                  onChange={(event) => update("street", event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Number
                <input
                  className={fieldClass}
                  value={draft.houseNumber}
                  onChange={(event) => update("houseNumber", event.target.value)}
                />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
              <label className="flex flex-col gap-1 text-sm">
                Postal code
                <input
                  className={fieldClass}
                  inputMode="numeric"
                  value={draft.postalCode}
                  onChange={(event) => update("postalCode", event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                City
                <input
                  className={fieldClass}
                  value={draft.city}
                  onChange={(event) => update("city", event.target.value)}
                />
              </label>
            </div>
            <p className="text-muted-foreground text-sm">Country: Germany</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                Phone
                <input
                  className={fieldClass}
                  type="tel"
                  value={draft.phone}
                  onChange={(event) => update("phone", event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Email
                <input
                  className={fieldClass}
                  type="email"
                  value={draft.email}
                  onChange={(event) => update("email", event.target.value)}
                />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                Latitude
                <input
                  className={fieldClass}
                  inputMode="decimal"
                  value={draft.latitude}
                  onChange={(event) => update("latitude", event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Longitude
                <input
                  className={fieldClass}
                  inputMode="decimal"
                  value={draft.longitude}
                  onChange={(event) => update("longitude", event.target.value)}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              Map link
              <input
                className={fieldClass}
                type="url"
                value={draft.mapUrl}
                onChange={(event) => update("mapUrl", event.target.value)}
              />
            </label>
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
            {message ? <p className="text-sm text-green-700">{message}</p> : null}
            <div>
              <Button type="button" disabled={saving} onClick={() => void save()}>
                {saving ? "Saving…" : "Save profile"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function HoursEditor({
  restaurantId,
  restaurantName,
  windows,
  onSaved,
}: {
  restaurantId: string;
  restaurantName: string;
  windows: OpeningWindow[];
  onSaved: (windows: OpeningWindow[]) => void;
}) {
  const [draft, setDraft] = useState<DayWindows[]>(() => hoursToDraft(windows));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(hoursToDraft(windows));
    setMessage(null);
    setError(null);
  }, [restaurantId, windows]);

  function updateDay(weekday: number, nextWindows: DayWindows["windows"]) {
    setDraft((current) =>
      current.map((day) => (day.weekday === weekday ? { ...day, windows: nextWindows } : day)),
    );
  }

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = RestaurantHoursSchema.parse(
        await apiPut<unknown>(`/dashboard/restaurants/${restaurantId}/hours`, {
          windows: draftToWindows(draft),
        }),
      );
      onSaved(payload.windows);
      setMessage("Opening hours saved.");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 400) {
        setError(
          "Invalid hours. Each window must open before it closes, and windows on the same day must not overlap.",
        );
      } else {
        setError("Could not save opening hours.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekly hours</CardTitle>
        <CardDescription>
          {restaurantName}. Multiple windows per day are allowed. An empty day is closed. Hours are
          local clock times (EUR / local, same as prices).
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {draft.map((day) => (
          <section key={day.weekday} className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-medium">{WEEKDAY_LABELS[day.weekday]}</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  updateDay(day.weekday, [...day.windows, { opensAt: "11:00", closesAt: "14:00" }])
                }
              >
                Add window
              </Button>
            </div>
            {day.windows.length === 0 ? (
              <p className="text-muted-foreground text-sm">Closed</p>
            ) : (
              <div className="flex flex-col gap-2">
                {day.windows.map((window, index) => (
                  <div
                    key={`${day.weekday}-${index}`}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Opens</span>
                      <input
                        type="time"
                        className={fieldClass}
                        value={window.opensAt}
                        onChange={(event) => {
                          const next = [...day.windows];
                          next[index] = { ...window, opensAt: event.target.value };
                          updateDay(day.weekday, next);
                        }}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Closes</span>
                      <input
                        type="time"
                        className={fieldClass}
                        value={window.closesAt}
                        onChange={(event) => {
                          const next = [...day.windows];
                          next[index] = { ...window, closesAt: event.target.value };
                          updateDay(day.weekday, next);
                        }}
                      />
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        updateDay(
                          day.weekday,
                          day.windows.filter((_, windowIndex) => windowIndex !== index),
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
        {error ? <p className="text-destructive text-sm">{error}</p> : null}
        {message ? <p className="text-sm text-green-700">{message}</p> : null}
        <div>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save week"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
